import clsx from 'clsx';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import TagWorkstreamModal from '@/src/components/project/Common/Modals/TagWorkstreamModal/TagWorkstreamModal';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { EmailEntry } from '@/src/components/project/Inbox2/types';
import { DEFAULT_DATE_FORMAT } from '@/src/config';
import { showToast } from '@/src/globalFunction';
import { queryClient } from '@/src/lib/react-query/react-query';
import {
  initialize,
  toggleModal,
} from '@/src/reducers/project/tagFileWorkstream/tagFileWorkstream.actions';
import { RootState } from '@/src/reducers/types';

import { useValidateActionResponse } from '../../../../../useValidateActionResponse';
import { emailEntriesKeys } from '../../../../api/getEmailEntries';
import { emailEntryKeys } from '../../../../api/getEmailEntry';
import { useTagEmailWorkstream } from '../../../../api/tagEmailWorkstream';

type TagEmailWorkstreamProps = {
  selectedEmailEntry: EmailEntry;
};

const TagEmailWorkstream = ({ selectedEmailEntry }: TagEmailWorkstreamProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const tagFileWorkstreamMutation = useTagEmailWorkstream({
    projectId,
    inboxEntryId: selectedEmailEntry.inboxEntryId,
    emailId: selectedEmailEntry.id,
  });
  const { confirmRefreshModal } = useValidateActionResponse({
    responseError: isResponseError(tagFileWorkstreamMutation.data)
      ? tagFileWorkstreamMutation.data
      : null,
  });
  const dispatch = useDispatch();
  const showModal = useSelector((store: RootState) => store.tagFileWorkstream.showModal);
  const selectedWorkstreams = useSelector((store: RootState) =>
    store.tagFileWorkstream.selectedWorkstreams.filter((ws) => !ws.isRemoved)
  );
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const document = useMemo(() => {
    return {
      id: selectedEmailEntry.id,
      linkId: selectedEmailEntry.inboxEntryId,
      origin: selectedEmailEntry.subject,
      type: 'thread',
      tagWorkstream: selectedEmailEntry.workstreams.map((ws) => ({
        id: ws.id,
        value: ws.id,
        color: ws.color,
        label: ws.name,
        isPermanent: ws.isPermanent,
      })),
    };
  }, [selectedEmailEntry]);

  useEffect(() => {
    if (showModal) {
      const documents = [document];

      dispatch(initialize(documents));
    }
  }, [showModal, dispatch, document]);

  const handleClose = () => {
    dispatch(toggleModal(false));
  };

  const handleOnWorkstreamsUpdated = () => {
    const modifiedDate = moment().format(DEFAULT_DATE_FORMAT);
    const modifiedBy = `${loggedUser.firstName} ${loggedUser.lastName}`;
    const newWorkstreams = selectedWorkstreams.map((ws) => ({
      ...ws,
      id: ws.value,
      color: ws.color,
      name: ws.label,
    }));

    queryClient.setQueriesData(
      [...emailEntriesKeys.lists({ projectId, inboxEntryId: selectedEmailEntry.inboxEntryId })],
      (prev: any) => {
        return {
          ...prev,
          pages: prev.pages.map((page) => {
            return {
              ...page,
              data: {
                ...page.data,
                emails: page.data.emails.map((emailEntry) => {
                  if (emailEntry.id === selectedEmailEntry.id) {
                    return {
                      ...emailEntry,
                      workstreams: newWorkstreams,
                      modifiedDate: modifiedDate,
                      modifiedBy: modifiedBy,
                    };
                  }
                  return emailEntry;
                }),
              },
            };
          }),
        };
      }
    );

    if (
      !queryClient.getQueryData([
        ...emailEntryKeys.detail({
          emailId: selectedEmailEntry.id,
          inboxEntryId: selectedEmailEntry.inboxEntryId,
          projectId,
        }),
      ])
    ) {
      return;
    }

    queryClient.setQueryData(
      [
        ...emailEntryKeys.detail({
          emailId: selectedEmailEntry.id,
          inboxEntryId: selectedEmailEntry.inboxEntryId,
          projectId,
        }),
      ],
      (prev: any) => {
        return {
          ...prev,
          data: {
            ...prev.data,
            workstreams: newWorkstreams.map((nws) => ({
              ...nws,
              'is-permanent':
                prev.data.workstreams.findIndex((ws) => ws.id === nws.id) >= 0
                  ? prev.data.workstreams.find((ws) => ws.id === nws.id)['is-permanent']
                  : false,
            })),
            modifiedDate: modifiedDate,
            modifiedBy: modifiedBy,
          },
        };
      }
    );
  };

  const handleSave = async () => {
    const selectedWorkstreamIds = selectedWorkstreams.map((ws) => ws.value);

    const response = await tagFileWorkstreamMutation.mutateAsync({
      inboxEntryId: selectedEmailEntry.inboxEntryId,
      emailId: selectedEmailEntry.id,
      projectId,
      workstreamIds: selectedWorkstreamIds,
    });

    if (response) {
      // Handle optimistic update away from onSuccess mutation because no workstream id in response
      handleOnWorkstreamsUpdated();
      handleClose();
      showToast('success', 'Email successfully tagged to the workstream.');
    }
  };

  const handleShowModal = async () => {
    dispatch(toggleModal(true));
  };

  return (
    <React.Fragment>
      <ActionButton
        id={'tag_document'}
        className={clsx('tw-w-full', 'tw-justify-between')}
        onClick={handleShowModal}
        label={'Add/Edit Tagged Workstream'}
        icon={ICONS.tag}
      />

      {showModal && (
        <ModalPortal>
          {tagFileWorkstreamMutation.isLoading && <Spinner />}
          <TagWorkstreamModal
            title="Tag a Workstream to this Email"
            onSave={handleSave}
            onClose={handleClose}
          />
        </ModalPortal>
      )}
      {confirmRefreshModal}
    </React.Fragment>
  );
};

export default TagEmailWorkstream;
